import type {
  BulkUserParams,
  BulkUserValidationRow,
  ChangeUserPasswordParamsWithId,
  DeleteUserParams,
  ListAutoCompleteUsersResponse,
  ListUsersResponse,
  UpdateUserParamsWithId,
  UserFiltersProps,
  UserModel,
  UserParams
} from '../types';
import { apiDel, apiGet, apiPatch, apiPost, apiPostFormData, apiPut } from './api-client';
import { PaginationParams } from './params';

const apiName = 'avaniV2HttpApi';
const path = 'users';

interface MyObject {
  [key: string]: any;
}

export const listUsersAutoComplete = async (
  search: string,
  filters?: Record<string, string | number>
): Promise<ListAutoCompleteUsersResponse> => {
  return apiGet(apiName, `${path}/autocomplete`, { search, ...filters });
};

export const listUsers = async (
  filters: UserFiltersProps,
  params: PaginationParams = new PaginationParams()
): Promise<ListUsersResponse> => {
  const { clients, venues, ...otherFilters } = filters;
  const validFilters = Object.entries(otherFilters).reduce<MyObject>(
    (acc, [key, value]) => {
      if (typeof value === 'string' && value.trim() !== '') {
        acc[key] = value;
      } else if (Array.isArray(value) && value.length > 0) {
        acc[key] = value.join(',');
      }
      return acc;
    },
    {
      ...(clients?.length && { clients: clients.map(({ id }) => id).join(',') }),
      ...(venues?.length && { venues: venues.map(({ id }) => id).join(',') })
    }
  );

  return apiGet(apiName, path, { ...params, ...validFilters });
};

export const getUserInfo = async (userId: string): Promise<UserModel> => {
  return apiGet(apiName, `${path}/${userId}`);
};

export const getUserInfoBySub = async (userSub: string): Promise<UserModel> => {
  return apiGet(apiName, `${path}/${userSub}/sub`);
};

export const createUser = async (body: UserParams): Promise<UserModel> => {
  return apiPost(apiName, path, body);
};

export const createUserBulk = async (body: BulkUserParams): Promise<UserModel> => {
  return apiPost(apiName, `${path}/bulk`, body);
};

export const validateUserBulkCsv = async (file: File): Promise<BulkUserValidationRow[]> => {
  const formData = new FormData();
  formData.append('file', file);
  return apiPostFormData(apiName, `${path}/bulk-csv`, formData);
};

export const changeUserPassword = async (params: ChangeUserPasswordParamsWithId): Promise<{ message: string }> => {
  const { userId, userPassword } = params;
  return apiPatch(apiName, `${path}/${userId}/change-password`, { userPassword });
};

export const updateUser = async (params: UpdateUserParamsWithId): Promise<UserModel> => {
  const { userId } = params;
  return apiPut(apiName, `${path}/${userId}`, params);
};

export const deleteUser = async (params: DeleteUserParams): Promise<UserModel> => {
  return apiDel(apiName, `${path}/${params.userId}`);
};

export const archiveUser = async (params: DeleteUserParams): Promise<UserModel> => {
  return apiPatch(apiName, `${path}/${params.userId}/archive`);
};

export const unarchiveUser = async (params: DeleteUserParams): Promise<UserModel> => {
  return apiPatch(apiName, `${path}/${params.userId}/unarchive`);
};

export const getUserMfa = async (userId: string): Promise<{ enabled: boolean }> => {
  return apiGet(apiName, `${path}/${userId}/mfa`);
};

export const disableUserMfa = async (userId: string): Promise<{ message: string }> => {
  return apiPatch(apiName, `${path}/${userId}/mfa/disable`);
};
